import { Injector, Pipe, PipeTransform } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Store } from '@ngxs/store';
import { isObservable, Observable, of as observableOf } from 'rxjs';
import { map } from 'rxjs/operators';
import { getCurrentRouteSnapshot } from '../../../helpers/utils';
import { ScEndpointReqScope } from '../../../models/table.model';
import { RestService } from '../../../services/rest-service.service';

interface ISelectOption {
  label: string;
  disabled?: boolean;
  value?: any;
  group?: ISelectOption[];
}

type ITransformOption = Partial<{
  labelProp: (option: any) => string;
  valueProp: (option: any) => any;
  disabledProp: (option: any) => boolean;
  groupProp: (option: any) => string;
}>;

@Pipe({ name: 'formFieldSelectOptions' })
export class FieldOptionsPipe implements PipeTransform {
  constructor(private _restService: RestService, private _injector: Injector, private _route: ActivatedRoute, private _store: Store) {}
  transform(options: any, field?: FormlyFieldConfig): Observable<ISelectOption[]> {
    if (Array.isArray(options)) {
      options = observableOf(options);
    } else if (isObservable(options)) {
    } else {
      options = options.call(this._helpers());
    }
    return (options as Observable<any>).pipe(map(value => this._transformOptions(value, field)));
  }

  private _helpers(): ScEndpointReqScope {
    return {
      routeSnapshot: getCurrentRouteSnapshot(this._route),
      injector: this._injector,
      rest: this._restService,
      store: this._store
    };
  }

  private _transformOptions(options: any[], field?: FormlyFieldConfig): ISelectOption[] {
    const to = this._transformSelectProps(field);

    const opts: ISelectOption[] = [];
    const groups = {};

    options.forEach(option => {
      const o = this._transformOption(option, to);
      if (o.group) {
        const id = groups[o.label];
        if (id === undefined) {
          groups[o.label] = opts.push(o) - 1;
        } else {
          o.group.forEach(o => opts[id].group.push(o));
        }
      } else {
        opts.push(o);
      }
    });

    if (field && field.templateOptions) {
      field.templateOptions._flatOptions = !Object.keys(groups).length;
    }

    return opts;
  }

  private _transformOption(option: any, to: ITransformOption): ISelectOption {
    const group = to.groupProp(option);
    if (Array.isArray(group)) {
      return {
        label: to.labelProp(option),
        group: group.map(opt => this._transformOption(opt, to))
      };
    }

    option = {
      label: to.labelProp(option),
      value: to.valueProp(option),
      disabled: !!to.disabledProp(option)
    };

    if (group) {
      return { label: group, group: [option] };
    }

    return option;
  }

  private _transformSelectProps(field: FormlyFieldConfig): ITransformOption {
    const to = field && field.templateOptions ? field.templateOptions : {};
    const selectPropFn = (prop: any) => (typeof prop === 'function' ? prop : o => o[prop]);

    return {
      groupProp: selectPropFn(to.groupProp || 'group'),
      labelProp: selectPropFn(to.labelProp || 'label'),
      valueProp: selectPropFn(to.valueProp || 'value'),
      disabledProp: selectPropFn(to.disabledProp || 'disabled')
    };
  }
}
